/**
 * Directional route/content transition for the app shell.
 *
 * On navigation the incoming screen slides in: forward (going deeper —
 * opening a work order, or moving right along the tab bar) enters from the
 * right; back (returning to a list / leftward tab) enters from the left.
 * Enter-only — the outgoing screen unmounts immediately (no held second
 * tree), which keeps it cheap on the forced lite-mode tablets. The chrome
 * (Topbar / BottomActionBar) stays put; only the routed content moves.
 *
 * `overflow-x-clip` prevents the 24px slide from ever spawning a horizontal
 * scrollbar. Under prefers-reduced-motion it degrades to an opacity fade.
 */
import { useEffect, useRef } from 'react'
import { useLocation, useOutlet } from 'react-router-dom'
import { useReducedMotion } from '@vollgas/shared/hooks'

// Destination "depth". Tabs rank left→right; detail screens sit deeper.
// Higher rank than the previous route = forward; lower = back.
function rankFor(pathname: string): number {
  if (pathname === '/') return 0
  if (pathname.startsWith('/inventory')) return 1
  if (pathname.startsWith('/services')) return 2
  if (pathname.startsWith('/profile')) return 3
  return 10 // work-order detail + anything deeper
}

export function RouteTransition() {
  const location = useLocation()
  const outlet = useOutlet()
  const reduced = useReducedMotion()
  const prevPathRef = useRef(location.pathname)

  // Compare against the last committed path (ref updated in the effect, so
  // this stays correct under StrictMode's double render).
  const forward = rankFor(location.pathname) >= rankFor(prevPathRef.current)
  useEffect(() => {
    prevPathRef.current = location.pathname
  }, [location.pathname])

  const anim = reduced
    ? 'animate-fade-in'
    : forward ? 'animate-route-in-right' : 'animate-route-in-left'

  return (
    <div className="overflow-x-clip">
      <div key={location.pathname} className={anim}>
        {outlet}
      </div>
    </div>
  )
}
